/**
 * /invite/:token: the far end of a God View magic link (F21).
 *
 * Not to be confused with screens/graduation/Claim.tsx (/claim/:token,
 * S21a, the Loop graduation flow) - a different feature reached by a
 * different link, kept in a different folder on purpose. This screen has
 * one job: turn a single-use, 30-day invite token into a real account. It
 * checks the token first (GET /invites/:token) so a person who followed an
 * expired or already-used link sees a plain sentence instead of a form
 * asking for a password that will fail on submit.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import { Button } from '../../components/primitives/Button';
import { Field } from '../../components/primitives/Field';
import { HeroBackdrop } from '../../components/patterns/HeroBackdrop';
import { Skeleton } from '../../components/patterns/Skeleton';
import { Wordmark } from '../../components/brand/Wordmark';

import { setToken, useSessionStore } from '../../lib/auth/session';
import { apiFetch, ApiError } from '../../lib/http';
import { t } from '../../lib/i18n/t';
import { captureEvent } from '../../lib/analytics/posthog';
import { useFormTracker } from '../../lib/analytics/use-form-tracker';

import '../../styles/screens/auth.css';
import type { Plan } from '../../lib/types';

interface InviteInfo {
  email: string;
  expires_at: string;
}

interface ClaimResponse {
  account: { id: string; email: string; display_name: string | null; mode: 'simple' | 'advanced'; language?: 'en' | 'th' | null; is_admin: boolean; plan_id?: Plan; plan_expires_at?: string | null; created_at: string };
  token: string;
}

function invalidLinkMessage(code: string | undefined): string {
  switch (code) {
    case 'INVITE_EXPIRED':
      return t('This invite link has expired. Ask an admin to send a new one.');
    case 'INVITE_ALREADY_USED':
      return t('This invite link has already been used.');
    case 'INVITE_REVOKED':
      return t('This invite link is no longer valid. Ask an admin to send a new one.');
    default:
      return t('This invite link is not valid.');
  }
}

export default function Claim() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const setAccount = useSessionStore((s) => s.setAccount);

  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const formTracker = useFormTracker({
    formId: 'invite-claim',
    formName: 'Claim Invite Token',
    totalFields: 1,
  });

  useEffect(() => {
    if (!token) return;
    apiFetch<InviteInfo>(`/api/v1/invites/${token}`, { unauthenticated: true })
      .then((data) => {
        setInvite(data);
        captureEvent('invite_token_verified');
      })
      .catch((err) => {
        const code = err instanceof ApiError ? err.code : 'unknown';
        captureEvent('invite_token_invalid', { error_code: code });
        setLoadError(invalidLinkMessage(err instanceof ApiError ? err.code : undefined));
      });
  }, [token]);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    setSubmitError(null);
    setBusy(true);
    formTracker.trackSubmit();
    apiFetch<ClaimResponse>(`/api/v1/invites/${token}/claim`, {
      method: 'POST',
      body: { password },
      unauthenticated: true,
    })
      .then((res) => {
        formTracker.trackSubmitSuccess();
        captureEvent('invite_claim_success');
        setToken(res.token);
        setAccount({
          id: res.account.id,
          name: res.account.display_name ?? res.account.email.split('@')[0] ?? res.account.email,
          email: res.account.email,
          plan: res.account.plan_id ?? 'basic',
          planExpiresAt: res.account.plan_expires_at ?? null,
          detailLevel: res.account.mode,
          language: res.account.language ?? null,
          createdAt: res.account.created_at,
          githubHandle: '',
          isAdmin: res.account.is_admin,
          // Real value arrives from getAccount() on shell mount; default off until then.
          tenantStagingEnabled: false,
          teamsEnabled: false,
          svgitEnabled: false,
        });
        navigate('/projects', { replace: true });
      })
      .catch((err) => {
        const code = err instanceof ApiError ? err.code : 'unknown';
        const msg = err instanceof ApiError ? err.message : t('Something went wrong. Try again in a moment.');
        formTracker.trackSubmitError(code, msg);
        captureEvent('invite_claim_failed', { error_code: code, error_message: msg });
        if (err instanceof ApiError && (err.code === 'INVITE_ALREADY_USED' || err.code === 'INVITE_EXPIRED' || err.code === 'INVITE_REVOKED')) {
          setLoadError(invalidLinkMessage(err.code));
          setInvite(null);
        } else {
          setSubmitError(msg);
        }
      })
      .finally(() => setBusy(false));
  }

  return (
    <div className="auth">
      <HeroBackdrop reach="tight" />
      <div className="auth__lockup">
        <Wordmark size={32} />
      </div>
      <div className="auth__card">
        <h1 className="auth__title">{t('Set up your SV Cloud account')}</h1>

        {!invite && !loadError && <Skeleton label={t('Checking your invite')} />}

        {loadError && (
          <>
            <p className="auth__say">{loadError}</p>
            <p className="auth__foot">
              <Link className="link-inline" to="/sign-in">
                {t('Back to sign in')}
              </Link>
            </p>
          </>
        )}

        {invite && (
          <>
            <p className="auth__say">{t('Choose a password for this account.')}</p>
            <form className="auth__wide" onSubmit={submit}>
              {/* A password manager needs a username field in the same form
                  to know WHICH account the password it is about to save
                  belongs to. Without one, Chrome and Google Password Manager
                  offer to save a password with no username and the person has
                  to type their address in by hand afterwards. Read only,
                  because the invite already decided the address, and visible
                  rather than hidden, because a hidden username field is a
                  heuristic while a visible one is a fact, and somebody setting
                  a password should see which account it is for. `name` and
                  `autoComplete` are both load bearing. */}
              <Field
                label={t('Email')}
                type="email"
                name="username"
                autoComplete="username"
                value={invite.email}
                readOnly
              />
              <Field
                label={t('Password')}
                name="new-password"
                type="password"
                value={password}
                onFocus={() => formTracker.onFieldFocus('password')}
                onBlur={() => formTracker.onFieldBlur('password')}
                onChange={(e) => {
                  setPassword(e.target.value);
                  formTracker.onFieldChange('password', e.target.value);
                }}
                autoComplete="new-password"
                minLength={8}
                error={submitError ?? undefined}
                required
              />
              <Button type="submit" variant="solid" loading={busy}>
                {t('Create my account')}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
